import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth.jsx";
import { Wordmark, Spine } from "../components/Brand.jsx";
import api from "../lib/api.js";
import { speakWithPersona } from "../lib/voice.js";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("demo@posturesteps.com");
  const [password, setPassword] = useState("Demo@2026!");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}.`);

      // Kick off today's coach progress report and play it inside this
      // user-gesture context so the browser allows the audio. Fire-and-forget;
      // we don't block navigation on it.
      const playNudge = async () => {
        try {
          // Get-or-create today's nudge
          const today = new Date().toISOString().slice(0, 10);
          const histRes = await api.get("/coach/history?limit=10");
          let text = histRes.data.find(
            (m) => m.role === "assistant" && m.kind === "nudge" && (m.created_at || "").startsWith(today)
          )?.text;
          if (!text) {
            const r = await api.post("/coach/nudge");
            text = r.data.text;
          }
          if (text) {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            // Only mark the day as "spoken" once playback actually starts —
            // otherwise a network/autoplay failure would permanently gate audio
            // for the rest of the day (the CoachCompanion autoplay handler
            // short-circuits when ps_nudge_audio_<today> === "1").
            speakWithPersona(text, {
              apiBase: base,
              onStart: () => {
                localStorage.setItem(`ps_nudge_audio_${today}`, "1");
              },
            });
          }
        } catch (err) { /* ignore — autoplay handler will retry on next gesture */ }
      };
      playNudge();

      if (u.role === "admin") nav("/admin");
      else if (!u.onboarded) nav("/onboarding");
      else nav("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#050E15] ps-aurora overflow-hidden">
      <div className="ps-grain absolute inset-0 pointer-events-none" />

      {/* Floating decorative spines */}
      <div className="absolute top-20 left-10 opacity-20 ps-float">
        <Spine size={48} />
      </div>
      <div className="absolute bottom-32 right-12 opacity-15 ps-float" style={{ animationDelay: "3s" }}>
        <Spine size={72} />
      </div>

      <div className="relative min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-sm ps-fade-up">
          <Link to="/" className="block mb-12">
            <Wordmark />
          </Link>

          <div className="ps-chip mb-6">Welcome back</div>
          <h1 className="ps-heading text-4xl font-extrabold leading-[1.05] tracking-tight">
            Walk strong.<br />
            <span className="ps-serif ps-italic font-normal ps-gradient-text">Today again.</span>
          </h1>
          <p className="text-slate-400 mt-4 ps-serif ps-italic">Sign in to keep the streak alive.</p>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Email</label>
              <input
                data-testid="login-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="ps-input mt-2"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Password</label>
              <input
                data-testid="login-password"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="ps-input mt-2"
              />
            </div>
            <button data-testid="login-submit" disabled={loading} className="ps-button-primary w-full inline-flex items-center justify-center gap-2">
              {loading ? "Signing in…" : (<>Log in <ArrowRight size={16} /></>)}
            </button>
          </form>

          <p className="mt-7 text-sm text-slate-400 text-center">
            New here? <Link to="/register" className="text-[#4ade80] font-semibold hover:text-[#22d3ee] transition-colors" data-testid="goto-register">Create an account</Link>
          </p>

          <div className="mt-8 ps-glass p-4 text-xs text-slate-400 ps-mono">
            <div className="font-bold text-white ps-heading mb-2 not-italic">Demo accounts</div>
            demo@posturesteps.com / Demo@2026!<br />
            admin@posturesteps.com / Admin@2026!
          </div>
        </div>
      </div>
    </div>
  );
}
